import { useMemo, type ReactNode } from 'react'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Race } from 'src/types/domain/Race'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { isBirdieGameType, isCCTVGameType, isCrashGameType } from 'src/utils/gameTypeUtils'
import { safeLocale } from 'src/utils/safeLocale'
import { BirdieRecentForm } from 'src/components/BirdieBoard/BirdieRecentForm'
import { MarbleItem } from 'src/components/MarbleItem'
import { useCountUp } from 'src/hooks/useCountUp'
import {
  buildCountingTotal,
  buildCrashStats,
  buildOutcomeStats,
  type CrashBucketKey
} from 'src/utils/roundStats'

type Props = Readonly<{
  game: MarbleGame
  /** The window the stats describe — already spoiler-filtered by the caller. */
  races: Race[]
  /** The deep read is still in flight. Renders a same-height skeleton so the
   *  rows below don't jump when the real window lands. */
  isLoading?: boolean
  /** Floating-panel dress: drops the title and the disclaimer, which the
   *  sidebar keeps. The panel is small, deliberately opened, and meant to sit
   *  open over live play — the sidebar has room to say more. */
  compact?: boolean
}>

// Same tiers the history pills wear, so a "2×–5×" row is the same green as a
// 3× pill sitting under it. Reusing the palette rather than inventing one.
const BUCKET_ACCENT: Record<CrashBucketKey, string> = {
  under2: 'round-stats__row--low',
  '2to5': 'round-stats__row--good',
  '5to10': 'round-stats__row--high',
  over10: 'round-stats__row--huge'
}

const formatPct = (pct: number): string => `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`

/**
 * The engine's own detector labels ('vehicles' | 'people' | 'motorcycles' |
 * 'ducks' | 'skiers'). Anything unrecognised — or absent, which happens
 * because one engine path clamps the type — falls through to generic phrasing
 * rather than inventing a noun.
 *
 * Written as one t() call per case rather than a lookup table: the i18n
 * extractor only matches string literals written directly inside the call, so
 * a computed key would ship untranslated in every locale and silently fall
 * back to English.
 */
const countedLabel = (detectionType: string | undefined, t: (key: string) => string): string => {
  switch (detectionType) {
    case 'vehicles':
      return t('vehicles counted')
    case 'people':
      return t('people counted')
    case 'motorcycles':
      return t('motorcycles counted')
    case 'ducks':
      return t('ducks counted')
    case 'skiers':
      return t('skiers counted')
    default:
      return t('counted in total')
  }
}

/** One stat row. The bar and the number ride the SAME animated value, so they
 *  can never disagree mid-flight. */
function StatRow({
  accent,
  count,
  label,
  pct
}: Readonly<{ accent?: string; count: number; label: ReactNode; pct: number }>) {
  const animatedPct = useCountUp(pct)

  return (
    <li className={`round-stats__row ${accent ?? ''}`}>
      {label}
      <span className="round-stats__track">
        <span className="round-stats__fill" style={{ width: `${animatedPct}%` }} />
      </span>
      <span className="round-stats__value">
        {formatPct(animatedPct)}
        <span className="round-stats__count">{count}</span>
      </span>
    </li>
  )
}

/** Same chrome, the same row count AND the same trailing block as the real
 *  card, so the swap is a cross-fade rather than a reflow of everything below
 *  it. Reserving the rows but not the crash "Biggest" chips still left a ~90px
 *  jump, which is the whole thing this exists to prevent. */
function StatsSkeleton({
  compact,
  rows,
  variant
}: Readonly<{ compact?: boolean; rows: number; variant: 'crash' | 'counting' | 'marble' }>) {
  return (
    <section className="round-stats round-stats--loading" aria-busy="true">
      <header className="round-stats__head">
        {!compact && <span className="round-stats__skeleton round-stats__skeleton--title" />}
        <span className="round-stats__skeleton round-stats__skeleton--window" />
      </header>
      <ul className="round-stats__rows">
        {Array.from({ length: rows }, (_, i) => (
          <li className="round-stats__row" key={i}>
            <span className="round-stats__label">
              <span className="round-stats__skeleton round-stats__skeleton--label" />
            </span>
            <span className="round-stats__track" />
            <span className="round-stats__value">
              <span className="round-stats__skeleton round-stats__skeleton--value" />
            </span>
          </li>
        ))}
      </ul>
      {variant === 'counting' && (
        <div className="round-stats__counted">
          <span className="round-stats__skeleton round-stats__skeleton--counted" />
        </div>
      )}
      {variant === 'crash' && (
        <div className="round-stats__hits">
          <span className="round-stats__skeleton round-stats__skeleton--hits-label" />
          {[0, 1, 2].map((i) => (
            <span className="round-stats__skeleton round-stats__skeleton--hit" key={i} />
          ))}
        </div>
      )}

      {!compact && (
        <p className="round-stats__note">
          <span className="round-stats__skeleton round-stats__skeleton--note" />
        </p>
      )}
    </section>
  )
}

export function RoundStats({ game, races, isLoading, compact }: Props) {
  const language = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(language)
  // `language` drives i18n (which supports `en-soc`); Intl does NOT accept it,
  // so number formatting below goes through the coerced locale instead.
  const locale = safeLocale(language)
  const isCrash = isCrashGameType(game.track.gameType)
  // B19b — the golf game takes this component's Birdie branch, below. Declared
  // here so the marble scan can be skipped for it; the branch itself renders
  // after the hooks, because every hook in this component has to run on every
  // render whichever game it is describing.
  const isBirdie = isBirdieGameType(game.track.gameType)

  // The panel re-renders on every socket tick while it is open; the window is
  // a few hundred rounds, so memoise rather than recount each time.
  const isCounting = !isCrash && isCCTVGameType(game.track.gameType)
  const crash = useMemo(() => (isCrash ? buildCrashStats(races) : undefined), [isCrash, races])
  const outcomes = useMemo(
    () => (isCrash || isBirdie ? undefined : buildOutcomeStats(races, game.marbles)),
    [isCrash, isBirdie, races, game.marbles]
  )
  const counted = useMemo(
    () => (isCounting ? buildCountingTotal(races) : undefined),
    [isCounting, races]
  )
  const sampleSize = crash?.sampleSize ?? outcomes?.sampleSize ?? 0
  const animatedTotal = useCountUp(counted?.total ?? 0)

  // B19b — the golf game's rows, in this component's place: the sidebar's top
  // block and the stats popover both reach it here, with the same window and the
  // same `compact` dress. Owner: a Birdie round's "which option won" means
  // nothing across rounds that are repriced every round, so its four rows are
  // how the three putts fell — none, one, two or all three in — over the same
  // last-50 window. B19's parallel popover is gone; the eleven other games never
  // take this branch.
  //
  // Above the loading skeleton on purpose: the Birdie panel always renders (four
  // honest zeros on a fresh game) and its window line reads whatever has landed,
  // so there is no row count to reserve and nothing to cross-fade. It is also
  // above the `sampleSize < 10` floor, which is a marble/crash judgement about
  // percentages this panel's own window line already qualifies.
  if (isBirdie) {
    return <BirdieRecentForm compact={compact} marbles={game.marbles} races={races} />
  }

  if (isLoading) {
    // Match the real card's row count so the entries below never reflow.
    // Count only what will actually be rendered — retired marbles are filtered
    // out of the real card, so counting them here would reserve rows the
    // loaded card never fills and reintroduce the jump.
    const activeMarbles = game.marbles.filter((m) => m.active).length
    const rows = isCrash ? 4 : Math.min(Math.max(activeMarbles, 3), 8)
    const variant = isCrash ? 'crash' : isCounting ? 'counting' : 'marble'
    return <StatsSkeleton compact={compact} rows={rows} variant={variant} />
  }

  // Below a handful of rounds the percentages say more about the sample than
  // about the game, so show nothing rather than something misleading.
  if (sampleSize < 10) {
    return null
  }

  return (
    <section className="round-stats" aria-label={t('Recent form')}>
      <header className="round-stats__head">
        {!compact && <span className="round-stats__title">{t('Recent form')}</span>}
        {/* Keyed on the count so a settling round remounts the node and
            replays the tick — the panel is meant to be left open, and this is
            what shows it is live rather than a snapshot. */}
        <span className="round-stats__window" key={sampleSize}>
          {t('Last {{count}} rounds', { count: sampleSize })}
        </span>
      </header>

      <ul className="round-stats__rows">
        {crash
          ? crash.buckets.map((bucket) => (
              <StatRow
                accent={BUCKET_ACCENT[bucket.key]}
                count={bucket.count}
                key={bucket.key}
                label={<span className="round-stats__label">{bucket.label}</span>}
                pct={bucket.pct}
              />
            ))
          : outcomes?.outcomes.map((outcome) => (
              <StatRow
                count={outcome.wins}
                key={outcome.marble.uuid}
                label={
                  <span className="round-stats__label round-stats__label--marble">
                    <MarbleItem marble={outcome.marble} />
                    <span>{getTranslatedMarbleColor(outcome.marble.color, t)}</span>
                  </span>
                }
                pct={outcome.pct}
              />
            ))}
        {outcomes?.other && (
          <StatRow
            accent="round-stats__row--other"
            count={outcomes.other.wins}
            key="other"
            label={<span className="round-stats__label">{t('Other')}</span>}
            pct={outcomes.other.pct}
          />
        )}
      </ul>

      {counted && counted.total > 0 && (
        <div className="round-stats__counted">
          <b>{Math.round(animatedTotal).toLocaleString(locale)}</b>
          <span>{countedLabel(counted.detectionType, t)}</span>
        </div>
      )}

      {crash && crash.highlights.length > 0 && (
        <div className="round-stats__hits">
          <span className="round-stats__hits-label">{t('Biggest')}</span>
          {crash.highlights.map((hit) => (
            <span className="round-stats__hit" key={hit.raceId}>
              <b>{hit.multiplier.toFixed(2)}×</b>
              <em>
                {hit.roundsAgo === 0
                  ? t('last round')
                  : t('{{count}} rounds ago', { count: hit.roundsAgo })}
              </em>
            </span>
          ))}
        </div>
      )}

      {!compact && (
        <p className="round-stats__note">{t('Past rounds do not predict future rounds.')}</p>
      )}
    </section>
  )
}
